// Account namespace — covers DashboardPage, SettingsPage, BillingPage.
// Keys are split into three sub-objects that mirror the view files, plus a
// small `shared` group for copy that appears in more than one view (e.g. the
// email-verification banner, the "Go to settings" CTA, "Invoice" link).
export default {
  en: {
    shared: {
      verifyEmailNotice: 'Verify your email to start purchasing keys.',
      verifyEmailNoticeBilling: 'Please verify your email to continue.',
      goToSettings: 'Go to settings',
      invoice: 'Invoice',
      loading: 'Loading…',
    },

    shell: {
      wallet: 'Wallet',
    },

    dashboard: {
      statsLabel: 'Account overview',
      accountFallback: 'Your account',
      memberSince: 'Member since {year}',
      editProfile: 'Edit profile',

      stats: {
        gamesOwned: 'Games owned',
        totalSpent: 'Total spent',
        wishlistItems: 'Wishlist',
        walletBalance: 'Wallet balance',
        lastOrder: 'Last order {date}',
        noPurchases: 'No purchases yet',
        viewWishlist: 'View wishlist',
        topUp: 'Top up',
      },

      library: {
        heading: 'My library',
        viewAll: 'View all',
        viewAllGames: 'View all {count} games',
        empty: 'No games yet.',
        browseCatalog: 'Browse the catalog',
      },

      orders: {
        heading: 'Recent orders',
        empty: 'No orders yet.',
      },
    },

    settings: {
      title: 'Account settings',
      subtitle: 'Manage your profile and preferences.',

      personalInfo: {
        heading: 'Personal info',
        labelName: 'Name',
        labelSurname: 'Surname',
        labelEmail: 'Email address',
        labelCountry: 'Country',
        labelBirthDate: 'Birth date',
        labelAddress: 'Address',
        labelCity: 'City',
        labelPostalCode: 'Postal code',
        labelPhone: 'Phone',
        placeholderGivenName: 'Given name',
        placeholderFamilyName: 'Family name',
        placeholderStreet: 'Street address',
        placeholderCity: 'City',
        placeholderPostal: 'Postal code',
        placeholderPhone: '+372 555 1234',
        emailVerified: 'Verified',
        emailUnverified: 'Unverified',
        saveChanges: 'Save changes',
        saved: 'Saved.',
        saveError: 'Could not save profile.',
      },

      security: {
        heading: 'Security',
        subtitle: 'Change password',
        labelCurrent: 'Current',
        labelNew: 'New',
        labelConfirm: 'Confirm',
        hint: 'Min 8 characters, with at least one letter and one digit.',
        updatePassword: 'Update password',
        updating: 'Updating…',
        errorWeak: 'New password must be at least 8 characters and contain a letter and a digit.',
        errorMismatch: 'New password and confirmation do not match.',
        success: 'Password updated. Other sessions have been signed out.',
        error: 'Could not change password.',
      },

      kyc: {
        heading: 'Identity verification',
        purchaseLimit: 'Required for purchases over €50.',
        statusApproved: 'KYC approved',
        statusPending: 'KYC under review',
        statusNone: 'KYC not started',
        bodyPending: 'Our compliance team is reviewing your submission. We will email you once a decision is made.',
        bodyApproved: 'Your identity is verified — your account has full access.',
        bodyNone: 'Complete identity verification to lift limits and unlock full access.',
        warningMatch: 'Important: Make sure your account personal information (name, address, date of birth) matches the details on your documents before submitting.',
        labelIdDoc: 'ID document (passport, ID card — up to 2 files)',
        labelProofOfAddress: 'Proof of address (utility bill, bank statement)',
        submitDocuments: 'Submit documents',
        requestReReview: 'Request re-review',
        submitting: 'Submitting…',
        successMessage: 'KYC review requested. Documents sent for review.',
        errorNoId: 'Please attach at least one ID document (passport or ID card).',
        errorNoAddress: 'Please attach a proof of address document.',
        errorFallback: 'Could not request KYC.',
      },

      danger: {
        heading: 'Danger zone',
        signOutTitle: 'Sign out of this device',
        signOutBody: 'You will need your password to sign in again.',
        deleteTitle: 'Delete account',
        deleteBody: 'Email support to close your account and remove your data.',
        deleteCta: 'Request deletion',
        deleteSubject: 'Account deletion request',
      },

      emailPrefs: {
        heading: 'Email preferences',
        body: 'Transactional emails (order receipts, key delivery, password resets) are always sent. Marketing emails aren\'t used yet.',
        unsubscribe: 'To unsubscribe entirely, contact',
        openRequest: 'Open a request',
      },
    },

    billing: {
      title: 'Billing & payments',

      stats: {
        totalSpent: 'Total spent',
        ordersThisYear: 'Orders this year',
      },

      wallet: {
        heading: 'Wallet',
        availableBalance: 'Available balance',
        heldAmount: '{amount} held in pending orders',
        topUpButton: 'Top up wallet',
        topUpDescription: 'Top up with card or PayPal via our secure payment provider. Funds are added instantly and refundable within 14 days.',
      },

      billingAddress: {
        heading: 'Billing address',
        empty: 'No billing address yet.',
        addInSettings: 'Add one in settings',
      },

      refunds: {
        heading: 'Refunds & disputes',
        body: 'Need help with a transaction? You can request a refund or dispute a charge within 14 days of purchase.',
        openRequest: 'Open a request',
      },

      invoices: {
        heading: 'Invoices',
        empty: 'No invoices yet. Every wallet top-up gets one.',
        colInvoice: 'Invoice',
        colDate: 'Date',
        colAmount: 'Amount',
        colActions: 'Actions',
        topUpVia: 'Wallet top-up via {method}',
      },

      orderHistory: {
        heading: 'Order history',
        empty: 'No orders yet.',
        colOrder: 'Order',
        colItem: 'Item',
        colPayment: 'Payment',
        colTotal: 'Total',
        colStatus: 'Status',
        colActions: 'Actions',
        paymentMethodWallet: 'Wallet',
        pagination: 'Page {page} of {total} · {count} orders',
        previous: 'Previous',
        next: 'Next',
      },

      walletActivity: {
        colDescription: 'Transaction',
        colDate: 'Date',
        colAmount: 'Amount',
        heading: 'Wallet activity',
        // deriveLabel-generated strings
        topUp: 'Top-up',
        holdForOrder: 'Hold for order {id}',
        orderHold: 'Order hold',
        paidOrder: 'Paid — order {id}',
        paymentReleased: 'Payment released',
        refundForOrder: 'Refund for order {id}',
        refund: 'Refund',
        purchaseOrder: 'Purchase — order {id}',
        purchase: 'Purchase',
        heldSuffix: 'held',
      },

      topupModal: {
        heading: 'Top up your wallet',
        close: 'Close',
        amountLabel: 'Amount',
        paymentMethodLabel: 'Payment method',
        methods: {
          TRUSTLY: 'Yapili',
          CARD: 'Credit / Debit card',
        },
        redirectNotice: "You'll be redirected to our secure payment provider.",
        continueToPayment: 'Continue to payment',
        redirecting: 'Redirecting…',
        devSkip: 'Dev: skip provider',
        devBusy: 'Dev top-up…',
        errorMinimum: 'Minimum top-up is €1.00.',
        errorBillingIncomplete: 'Please complete your billing info in Settings before topping up.',
        errorStart: 'Could not start top-up',
        errorFailed: 'Top-up failed',
      },
    },
  },

  cs: {
    shared: {
      verifyEmailNotice: 'Ověřte svůj e-mail, abyste mohli nakupovat klíče.',
      verifyEmailNoticeBilling: 'Prosím ověřte svůj e-mail, abyste mohli pokračovat.',
      goToSettings: 'Přejít do nastavení',
      invoice: 'Faktura',
      loading: 'Načítání…',
    },

    shell: {
      wallet: 'Peněženka',
    },

    dashboard: {
      statsLabel: 'Přehled účtu',
      accountFallback: 'Váš účet',
      memberSince: 'Člen od {year}',
      editProfile: 'Upravit profil',

      stats: {
        gamesOwned: 'Vlastněné hry',
        totalSpent: 'Celkem utraceno',
        wishlistItems: 'Seznam přání',
        walletBalance: 'Zůstatek peněženky',
        lastOrder: 'Poslední objednávka {date}',
        noPurchases: 'Zatím žádné nákupy',
        viewWishlist: 'Zobrazit seznam přání',
        topUp: 'Dobít',
      },

      library: {
        heading: 'Moje knihovna',
        viewAll: 'Zobrazit vše',
        viewAllGames: 'Zobrazit všechny {count} hry',
        empty: 'Zatím žádné hry.',
        browseCatalog: 'Prohlédněte si katalog',
      },

      orders: {
        heading: 'Nedávné objednávky',
        empty: 'Zatím žádné objednávky.',
      },
    },

    settings: {
      title: 'Nastavení účtu',
      subtitle: 'Spravujte svůj profil a preference.',

      personalInfo: {
        heading: 'Osobní údaje',
        labelName: 'Jméno',
        labelSurname: 'Příjmení',
        labelEmail: 'E-mailová adresa',
        labelCountry: 'Země',
        labelBirthDate: 'Datum narození',
        labelAddress: 'Adresa',
        labelCity: 'Město',
        labelPostalCode: 'PSČ',
        labelPhone: 'Telefon',
        placeholderGivenName: 'Křestní jméno',
        placeholderFamilyName: 'Příjmení',
        placeholderStreet: 'Ulice a číslo domu',
        placeholderCity: 'Město',
        placeholderPostal: 'PSČ',
        placeholderPhone: '+420 601 234 567',
        emailVerified: 'Ověřeno',
        emailUnverified: 'Neověřeno',
        saveChanges: 'Uložit změny',
        saved: 'Uloženo.',
        saveError: 'Profil se nepodařilo uložit.',
      },

      security: {
        heading: 'Zabezpečení',
        subtitle: 'Změnit heslo',
        labelCurrent: 'Stávající',
        labelNew: 'Nové',
        labelConfirm: 'Potvrdit',
        hint: 'Minimálně 8 znaků, alespoň jedno písmeno a jedna číslice.',
        updatePassword: 'Aktualizovat heslo',
        updating: 'Aktualizuje se…',
        errorWeak: 'Nové heslo musí mít alespoň 8 znaků a obsahovat písmeno a číslici.',
        errorMismatch: 'Nové heslo a jeho potvrzení se neshodují.',
        success: 'Heslo bylo aktualizováno. Ostatní relace byly odhlášeny.',
        error: 'Heslo se nepodařilo změnit.',
      },

      kyc: {
        heading: 'Ověření totožnosti',
        purchaseLimit: 'Vyžadováno pro nákupy nad 50 €.',
        statusApproved: 'KYC schváleno',
        statusPending: 'KYC probíhá',
        statusNone: 'KYC nezahájeno',
        bodyPending: 'Náš tým pro dodržování předpisů prověřuje vaše podání. O výsledku vás budeme informovat e-mailem.',
        bodyApproved: 'Vaše totožnost je ověřena — váš účet má plný přístup.',
        bodyNone: 'Dokončete ověření totožnosti a odstraňte limity pro plný přístup k účtu.',
        warningMatch: 'Důležité: Před odesláním zkontrolujte, zda osobní údaje na účtu (jméno, adresa, datum narození) odpovídají údajům v dokladech.',
        labelIdDoc: 'Průkaz totožnosti (cestovní pas, občanský průkaz — až 2 soubory)',
        labelProofOfAddress: 'Doklad o adrese (vyúčtování energií, výpis z bankovního účtu)',
        submitDocuments: 'Odeslat doklady',
        requestReReview: 'Požádat o opětovné přezkoumání',
        submitting: 'Odesílá se…',
        successMessage: 'Žádost o KYC odeslána. Dokumenty jsou předány k posouzení.',
        errorNoId: 'Prosím přiložte alespoň jeden průkaz totožnosti (pas nebo občanský průkaz).',
        errorNoAddress: 'Prosím přiložte doklad o adrese.',
        errorFallback: 'Žádost o KYC se nepodařilo odeslat.',
      },

      danger: {
        heading: 'Nebezpečná zóna',
        signOutTitle: 'Odhlásit se z tohoto zařízení',
        signOutBody: 'Pro opětovné přihlášení budete potřebovat heslo.',
        deleteTitle: 'Smazat účet',
        deleteBody: 'Napište podpoře a my váš účet uzavřeme a odstraníme vaše data.',
        deleteCta: 'Požádat o smazání',
        deleteSubject: 'Žádost o smazání účtu',
      },

      emailPrefs: {
        heading: 'E-mailové předvolby',
        body: 'Transakční e-maily (potvrzení objednávek, doručení klíčů, resetování hesla) jsou odesílány vždy. Marketingové e-maily zatím nepoužíváme.',
        unsubscribe: 'Pro úplné odhlášení kontaktujte',
        openRequest: 'Otevřít žádost',
      },
    },

    billing: {
      title: 'Fakturace a platby',

      stats: {
        totalSpent: 'Celkem utraceno',
        ordersThisYear: 'Objednávky tento rok',
      },

      wallet: {
        heading: 'Peněženka',
        availableBalance: 'Dostupný zůstatek',
        heldAmount: '{amount} blokováno v čekajících objednávkách',
        topUpButton: 'Dobít peněženku',
        topUpDescription: 'Dobijte kartou nebo přes PayPal prostřednictvím našeho bezpečného platebního poskytovatele. Prostředky jsou připsány okamžitě a jsou refundovatelné do 14 dnů.',
      },

      billingAddress: {
        heading: 'Fakturační adresa',
        empty: 'Zatím žádná fakturační adresa.',
        addInSettings: 'Přidejte ji v nastavení',
      },

      refunds: {
        heading: 'Vrácení plateb a spory',
        body: 'Potřebujete pomoc s transakcí? Můžete požádat o vrácení peněz nebo napadnout poplatek do 14 dnů od nákupu.',
        openRequest: 'Otevřít žádost',
      },

      invoices: {
        heading: 'Faktury',
        empty: 'Zatím žádné faktury. Každé dobití peněženky jednu dostane.',
        colInvoice: 'Faktura',
        colDate: 'Datum',
        colAmount: 'Částka',
        colActions: 'Akce',
        topUpVia: 'Dobití peněženky přes {method}',
      },

      orderHistory: {
        heading: 'Historie objednávek',
        empty: 'Zatím žádné objednávky.',
        colOrder: 'Objednávka',
        colItem: 'Položka',
        colPayment: 'Platba',
        colTotal: 'Celkem',
        colStatus: 'Stav',
        colActions: 'Akce',
        paymentMethodWallet: 'Peněženka',
        pagination: 'Strana {page} z {total} · {count} objednávek',
        previous: 'Předchozí',
        next: 'Další',
      },

      walletActivity: {
        colDescription: 'Transakce',
        colDate: 'Datum',
        colAmount: 'Částka',
        heading: 'Aktivita peněženky',
        topUp: 'Dobití',
        holdForOrder: 'Blokace pro objednávku {id}',
        orderHold: 'Blokace objednávky',
        paidOrder: 'Zaplaceno — objednávka {id}',
        paymentReleased: 'Platba uvolněna',
        refundForOrder: 'Vrácení za objednávku {id}',
        refund: 'Vrácení platby',
        purchaseOrder: 'Nákup — objednávka {id}',
        purchase: 'Nákup',
        heldSuffix: 'blokováno',
      },

      topupModal: {
        heading: 'Dobít peněženku',
        close: 'Zavřít',
        amountLabel: 'Částka',
        paymentMethodLabel: 'Způsob platby',
        methods: {
          TRUSTLY: 'Yapili',
          CARD: 'Kreditní / Debetní karta',
        },
        redirectNotice: 'Budete přesměrováni k našemu bezpečnému platebnímu poskytovateli.',
        continueToPayment: 'Pokračovat k platbě',
        redirecting: 'Přesměrovávám…',
        devSkip: 'Dev: přeskočit poskytovatele',
        devBusy: 'Dev dobíjení…',
        errorMinimum: 'Minimální dobití je 1,00 €.',
        errorBillingIncomplete: 'Před dobíjením prosím vyplňte fakturační údaje v Nastavení.',
        errorStart: 'Dobití se nepodařilo zahájit',
        errorFailed: 'Dobití se nezdařilo',
      },
    },
  },
};
